import { useEffect, useState } from 'react';
import { listAuditLogs } from '../../api/audit';
import type { AuditLogEntry } from '../../api/audit';
import { useTranslation } from 'react-i18next';
import styles from './ActivityFeed.module.css';

const ACTION_KEYS: Record<string, string> = {
  create_project: 'createdProject',
  update_project: 'updatedProject',
  delete_project: 'deletedProject',
  create_user: 'createdUser',
  update_user: 'updatedUser',
  deactivate_user: 'deactivatedUser',
  create_org: 'createdOrganization',
  update_org: 'updatedOrganization',
  delete_org: 'deactivatedOrganization',
  save_snapshot: 'savedVersion',
  rollback_snapshot: 'rolledBackVersion',
  login: 'loggedIn',
};

const ACTION_COLORS: Record<string, string> = {
  create_project: '#6366f1',
  update_project: '#22d3ee',
  delete_project: 'var(--color-danger)',
  create_user: '#6366f1',
  deactivate_user: 'var(--color-danger)',
  save_snapshot: '#6366f1',
  rollback_snapshot: '#f39c12',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString('fr-CH');
}

interface Props {
  resourceType?: string;
  resourceId?: string;
  limit?: number;
  compact?: boolean;
  /** Pre-fetched entries — when provided, skips internal API call */
  entries?: AuditLogEntry[];
}

export function ActivityFeed({ resourceType, resourceId, limit = 20, compact = false, entries: externalEntries }: Props) {
  const { t } = useTranslation('activity');
  const [internalEntries, setInternalEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(!externalEntries);

  useEffect(() => {
    if (externalEntries) return;
    listAuditLogs({ resource_type: resourceType, resource_id: resourceId, limit })
      .then(setInternalEntries)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resourceType, resourceId, limit, externalEntries]);

  const entries = externalEntries ?? internalEntries;

  if (loading) return <div className={styles.loading}>{t('loadingActivity')}</div>;
  if (entries.length === 0) return <div className={styles.empty}>{t('noActivityYet')}</div>;

  return (
    <div className={compact ? styles.compact : styles.feed}>
      {entries.map((entry) => {
        const key = ACTION_KEYS[entry.action]; const label = key ? t(key) : entry.action;
        const color = ACTION_COLORS[entry.action] ?? 'var(--color-text-muted)';
        const detail = entry.details as Record<string, string> | null;
        const name = detail?.name ?? detail?.email ?? entry.resource_id?.slice(0, 12) ?? '';

        return (
          <div key={entry.id} className={styles.item}>
            <div className={styles.dot} style={{ background: color }} />
            <div className={styles.content}>
              <div className={styles.action}>
                <span style={{ color }}>{label}</span>
                {name && <span className={styles.resourceName}>{name}</span>}
              </div>
              {!compact && detail?.change_summary && (
                <div className={styles.summary}>{detail.change_summary}</div>
              )}
              <div className={styles.meta}>
                <span>{entry.user_id?.slice(0, 8) ?? t('system')}</span>
                <span>{formatTime(entry.created_at)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
